import { Constants } from '../../internal/Constants';
import { Localized } from '../../internal/Localized';
import { useTheme } from '../../providers/ThemeProvider';
import { SharedFooter } from '../shared/SharedFooter';
import Styles from './Landing.module.css';
import { ILandingOption } from './LandingConstants';
import { Background, Button, Image, Label, Textbox, useAuth } from '@app/engine';
import { GetSupportedCountriesList, INicheContentDTO, IPlanDTO, IsValidE164, Logger, NormalizePhoneE164 } from '@shared/common';
import { GetUserGeoTimezone, Telemetry, UserClient } from '@shared/frontend';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

/** File-local constants for SignupCard. */
const localConstants = {
  logContext: 'SignupCard',
  emailPattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  areaCodesMaxLength: 18,
  // Keys used for the staged onboarding state captured server-side (signup session).
  stateKeyContactName: 'contactName',
  stateKeyContactPhone: 'contactPhone',
  stateKeyBusiness: 'business',
  stateKeyAreaCodes: 'areaCodes',
  stateKeyTrade: 'trade',
  stateKeyPlan: 'plan',
  stateKeyCountryCode: 'countryCode',
  stateKeyTimezone: 'timezone'
} as const;

/** Props for the SignupCard. */
interface ISignupCardProps {
  plans: IPlanDTO[];
  niches: INicheContentDTO[];
  selectedPlanCode: string;
  // Controlled country selection, lifted to Landing so the pricing card's currency reacts to changes here.
  countryCode: string;
  onCountryChange: (code: string) => void;
}

/** The collected signup form values. */
interface ISignupForm {
  contactName: string;
  contactPhone: string;
  business: string;
  areaCodes: string;
  email: string;
  trade: string;
  plan: string;
  countryCode: string;
  timezone: string;
}

/** Serialised shape stored in sessionStorage so the landing and onboarding forms stay in sync across navigation. */
interface ISignupDraft {
  contactName: string;
  contactPhone: string;
  business: string;
  areaCodes: string;
  email: string;
  trade: string;
  plan: string;
  countryCode: string;
  timezone: string;
}

/**
 * Reads the shared signup draft from session storage, returning null when absent or corrupt.
 *
 * @returns The parsed draft, or null.
 */
function ReadSignupDraft(): ISignupDraft | null {
  try {
    const raw = sessionStorage.getItem(Constants.SignupDraftStorageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ISignupDraft>;
    return {
      contactName: typeof parsed.contactName === 'string' ? parsed.contactName : '',
      contactPhone: typeof parsed.contactPhone === 'string' ? parsed.contactPhone : '',
      business: typeof parsed.business === 'string' ? parsed.business : '',
      areaCodes: typeof parsed.areaCodes === 'string' ? parsed.areaCodes : '',
      email: typeof parsed.email === 'string' ? parsed.email : '',
      trade: typeof parsed.trade === 'string' ? parsed.trade : '',
      plan: typeof parsed.plan === 'string' ? parsed.plan : '',
      countryCode: typeof parsed.countryCode === 'string' ? parsed.countryCode : '',
      timezone: typeof parsed.timezone === 'string' ? parsed.timezone : ''
    };
  } catch (err: unknown) {
    Logger.Error('SignupCard', `Failed to read signup draft: ${Logger.GetErrorMessage(err)}`);
    return null;
  }
}

/**
 * Writes the shared signup draft to session storage.
 *
 * @param draft - The draft to persist.
 */
function WriteSignupDraft(draft: ISignupDraft): void {
  try {
    sessionStorage.setItem(Constants.SignupDraftStorageKey, JSON.stringify(draft));
  } catch (err: unknown) {
    Logger.Error('SignupCard', `Failed to write signup draft: ${Logger.GetErrorMessage(err)}`);
  }
}

/**
 * Success panel shown after the demo signup is submitted.
 *
 * @param props - Component props.
 * @param props.onContinue - Invoked when the visitor proceeds to step 2 (dashboard account creation).
 * @returns The success panel element.
 */
function SignupSuccess({ onContinue }: { onContinue: () => void }) {
  return (
    <div className={Styles.signupSuccess}>
      <div className={Styles.successIcon} aria-hidden="true">
        ✓
      </div>
      <Label text={Localized.LandingSignupSuccessTitle} classNameContainer={Styles.successTitle} />
      <div className={Styles.successSteps}>
        <div className={`${Styles.successStep}`}>
          <span className={`${Styles.stepDot} ${Styles.stepDotDone}`} aria-hidden="true">
            ✓
          </span>
          <Label text={Localized.LandingSignupSuccessStep1} />
        </div>
        <div className={`${Styles.successStep} ${Styles.successStepActive}`}>
          <span className={`${Styles.stepDot} ${Styles.stepDotActive}`} aria-hidden="true" />
          <Label text={Localized.LandingSignupSuccessSetPassword} />
        </div>
        <div className={Styles.successStep}>
          <span className={Styles.stepDot} aria-hidden="true" />
          <Label text={Localized.LandingSignupSuccessStep2} />
        </div>
        <div className={Styles.successStep}>
          <span className={Styles.stepDot} aria-hidden="true" />
          <Label text={Localized.LandingSignupSuccessStep3} />
        </div>
      </div>
      <Button text={Localized.LandingSignupSuccessCTA} classNameButtonEnabled={Styles.formSubmit} onClickHandler={onContinue} />
    </div>
  );
}

/**
 * Card 6 — signup. Demo activation form that resolves to a success panel. Structured so a real backend submission can be wired in later.
 *
 * @param props - Component props.
 * @returns The signup card element.
 */
export function SignupCard(props: ISignupCardProps) {
  // Trade options come from the active niche catalog (already translated), keyed by niche slug.
  const tradeOptions = useMemo<ILandingOption[]>(() => props.niches.map((entry) => ({ key: entry.niche, label: entry.signupLabel })), [props.niches]);
  const { getThemedAssetDTO } = useTheme();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  // Onboarding-complete artwork shown in place of the activation form once the visitor is already authenticated.
  const readyAssetDTO = useMemo(() => getThemedAssetDTO(Constants.Bundle.Preload, 'Ready.png'), [getThemedAssetDTO]);
  const readyBackAssetDTO = useMemo(() => getThemedAssetDTO(Constants.Bundle.Preload, 'ReadyBack.png'), [getThemedAssetDTO]);
  const planOptions = useMemo<ILandingOption[]>(
    () =>
      // Names only: plan pricing lives on the landing pricing card (and, post-signup, the dashboard billing panel), so the selector never has to track the visitor's currency.
      props.plans.map((plan) => ({
        key: plan.code,
        label: plan.name
      })),
    [props.plans]
  );
  const defaultPlanCode = useMemo(() => {
    const featured = props.plans.find((plan) => plan.featured) ?? props.plans[0];
    return featured?.code ?? '';
  }, [props.plans]);

  const [form, setForm] = useState<ISignupForm>(() => {
    const draft = ReadSignupDraft();
    return {
      contactName: draft?.contactName ?? '',
      contactPhone: draft?.contactPhone ?? '',
      business: draft?.business ?? '',
      areaCodes: draft?.areaCodes ?? '',
      email: draft?.email ?? '',
      trade: draft?.trade ?? '',
      plan: draft?.plan ?? '',
      countryCode: draft?.countryCode ?? props.countryCode,
      timezone: draft?.timezone ?? GetUserGeoTimezone()
    };
  });
  const [error, setError] = useState<string>('');
  const [submitted, setSubmitted] = useState<boolean>(false);

  useEffect(() => {
    Telemetry.FeatureUsed(isAuthenticated ? 'landing_signup_authenticated_view' : 'landing_signup_form_view');
  }, [isAuthenticated]);

  // Mirror a plan chosen from the pricing card into the form's plan select.
  useEffect(() => {
    if (props.selectedPlanCode) setForm((prev) => ({ ...prev, plan: props.selectedPlanCode }));
  }, [props.selectedPlanCode]);

  // Keep the lifted Landing country in sync with the draft. When the draft loads with a country, report it upward so the pricing card currency matches.
  useEffect(() => {
    if (form.countryCode && form.countryCode !== props.countryCode) {
      props.onCountryChange(form.countryCode);
    }
    // Only run on initial mount/draft hydration; subsequent country changes are handled by the select onChange.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const update = (field: keyof ISignupForm) => (value: string) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      WriteSignupDraft(next);
      return next;
    });
  };

  const updateCountry = (code: string) => {
    Telemetry.FeatureUsed('landing_signup_country_changed');
    setForm((prev) => {
      const next = { ...prev, countryCode: code };
      WriteSignupDraft(next);
      return next;
    });
    props.onCountryChange(code);
  };

  // Hands the entered email to the dashboard Create-account form and navigates to step 2. The shared draft
  // already holds the full form, so only the email needs a one-shot handoff to the auth screen.
  const handleContinue = () => {
    Telemetry.FeatureUsed('landing_signup_continue');
    sessionStorage.setItem(Constants.SignupPrefillStorageKey, form.email);
    // Stage the selected country so the onboarding account step and the landing pricing card share the same country/currency when the visitor navigates back and forth.
    if (form.countryCode.trim().length > 0) sessionStorage.setItem(Constants.SetupBillingCountryStorageKey, form.countryCode.trim());
    else sessionStorage.removeItem(Constants.SetupBillingCountryStorageKey);
    navigate(Constants.Route.Home);
  };

  // An authenticated visitor never sees the activation form (see render) — this advances them straight to their dashboard.
  const handleContinueToDashboard = () => {
    Telemetry.FeatureUsed('landing_signup_ready_continue');
    navigate(Constants.Route.Home);
  };

  const handleSubmit = () => {
    const required = [form.contactName, form.contactPhone, form.business, form.email, form.trade, form.areaCodes];
    if (required.some((value) => value.trim().length === 0)) {
      setError(Localized.LandingSignupErrorRequired);
      return;
    }
    if (!localConstants.emailPattern.test(form.email)) {
      setError(Localized.LandingSignupErrorEmail);
      return;
    }
    if (!IsValidE164(NormalizePhoneE164(form.contactPhone))) {
      setError(Localized.LandingSignupErrorPhone);
      return;
    }
    setError('');
    Telemetry.FeatureUsed('landing_signup_submit');
    // Capture the staged onboarding form server-side (signup session, keyed by email, TTL-expired). Best-effort: a network failure must not block the demo success panel or the dashboard handoff.
    // The LOCAL draft deliberately survives submit: the server staging no-ops for an already-registered email (anti-enumeration), so for a visitor who ends up logging in instead, the draft is
    // the only copy left for the onboarding wizard. The draft is cleared when the account step completes (or with the tab session), never here.
    const controller = new AbortController();
    // Carry the effective plan — the visitor's explicit pick, or the default the select shows when untouched — so the onboarding "Create your business" step prefills it like every other field.
    const effectivePlan = form.plan || defaultPlanCode;
    const state: Record<string, string> = {
      [localConstants.stateKeyContactName]: form.contactName,
      [localConstants.stateKeyContactPhone]: NormalizePhoneE164(form.contactPhone),
      [localConstants.stateKeyBusiness]: form.business,
      [localConstants.stateKeyAreaCodes]: form.areaCodes,
      [localConstants.stateKeyTrade]: form.trade,
      [localConstants.stateKeyPlan]: effectivePlan,
      [localConstants.stateKeyCountryCode]: form.countryCode,
      [localConstants.stateKeyTimezone]: form.timezone
    };
    void UserClient.CaptureSignup(controller.signal, form.email, state).catch((err: unknown) => {
      Logger.Error(localConstants.logContext, Logger.GetErrorMessage(err));
    });
    setSubmitted(true);
  };

  return (
    <section className={Styles.signupCard}>
      <Background assetDTO={readyBackAssetDTO} classNameContainer={Styles.signupCardReadyBack} />
      <div className={Styles.cardTag}>
        <Label text={Localized.SharedGetStarted} classNameContainer={Styles.sectionLabel} />
        {/* Structural wrapper: a single Label cannot render the two-line styled headline, so a div is used and each line carries the tooltip. */}
        <div className={Styles.sectionTitle}>
          <Label text={Localized.LandingSignupTitle1} title={`${Localized.LandingSignupTitle1} ${Localized.LandingSignupTitle2}`} />
          <br />
          <Label text={Localized.LandingSignupTitle2} title={`${Localized.LandingSignupTitle1} ${Localized.LandingSignupTitle2}`} classNameContainer={Styles.accent} />
        </div>
      </div>
      <div className={`${Styles.cardBody} ${Styles.signupContent}`}>
        <div className={Styles.signupWrap}>
          {isAuthenticated ? (
            <div className={Styles.readyPanel}>
              <Image
                title={`${Localized.LandingSignupTitle1} ${Localized.LandingSignupTitle2}`}
                assetDTO={readyAssetDTO}
                classNameContainer={Styles.readyImageContainer}
                classNameImageEnabled={Styles.readyImage}
              />
              <Button text={`${Localized.LandingReadyContinue} →`} classNameButtonEnabled={`${Styles.formSubmit} ${Styles.ctaScrew}`} onClickHandler={handleContinueToDashboard} />
            </div>
          ) : submitted ? (
            <SignupSuccess onContinue={handleContinue} />
          ) : (
            <div className={Styles.signupBox}>
              <Label text={Localized.LandingSignupFormTitle} classNameContainer={Styles.signupTitle} />
              <Label text={Localized.LandingSignupFormSub} classNameContainer={Styles.signupSub} />
              <div className={Styles.formRow}>
                <div className={Styles.formGroup}>
                  <Label text={Localized.LandingSignupContactName} classNameContainer={Styles.formLabel} />
                  <Textbox
                    value={form.contactName}
                    onChange={update('contactName')}
                    placeholder={Localized.LandingSignupContactNamePlaceholder}
                    classNameContainer={Styles.formInputContainer}
                    classNameText={Styles.formInput}
                  />
                </div>
                <div className={Styles.formGroup}>
                  <Label text={Localized.SharedContactPhone} classNameContainer={Styles.formLabel} />
                  <Textbox
                    value={form.contactPhone}
                    onChange={update('contactPhone')}
                    onBlur={() => setForm((prev) => ({ ...prev, contactPhone: NormalizePhoneE164(prev.contactPhone) }))}
                    type="tel"
                    placeholder={Localized.SharedPhoneExample}
                    classNameContainer={Styles.formInputContainer}
                    classNameText={Styles.formInput}
                  />
                </div>
              </div>
              <div className={Styles.formRow}>
                <div className={Styles.formGroup}>
                  <Label text={Localized.LandingSignupBusiness} classNameContainer={Styles.formLabel} />
                  <Textbox
                    value={form.business}
                    onChange={update('business')}
                    placeholder={Localized.LandingSignupBusinessPlaceholder}
                    classNameContainer={Styles.formInputContainer}
                    classNameText={Styles.formInput}
                  />
                </div>
                <div className={Styles.formGroup}>
                  <Label text={Localized.LandingSignupEmail} classNameContainer={Styles.formLabel} />
                  <Textbox
                    value={form.email}
                    onChange={update('email')}
                    type="email"
                    placeholder={Localized.LandingSignupEmailPlaceholder}
                    classNameContainer={Styles.formInputContainer}
                    classNameText={Styles.formInput}
                  />
                </div>
              </div>
              <div className={Styles.formRow}>
                <div className={Styles.formGroup}>
                  <Label text={Localized.LandingSignupAreaCodes} classNameContainer={Styles.formLabel} />
                  <Textbox
                    value={form.areaCodes}
                    onChange={update('areaCodes')}
                    placeholder={Localized.LandingSignupAreaCodesPlaceholder}
                    maxLength={localConstants.areaCodesMaxLength}
                    classNameContainer={Styles.formInputContainer}
                    classNameText={Styles.formInput}
                  />
                </div>
                <div className={Styles.formGroup}>
                  <Label text={Localized.OnboardingCountry} classNameContainer={Styles.formLabel} />
                  <div className={Styles.formSelectWrap}>
                    <select className={Styles.formSelect} value={form.countryCode} onChange={(event) => updateCountry(event.target.value)}>
                      {GetSupportedCountriesList().map((country) => (
                        <option key={country.code} value={country.code}>
                          {(Localized as Record<string, string>)[country.labelKey] ?? country.code}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              <div className={Styles.formRow}>
                <div className={Styles.formGroup}>
                  <Label text={Localized.LandingSignupTrade} classNameContainer={Styles.formLabel} />
                  <div className={Styles.formSelectWrap}>
                    <select
                      className={Styles.formSelect}
                      value={form.trade}
                      onChange={(event) => {
                        Telemetry.FeatureUsed('landing_signup_trade_changed');
                        update('trade')(event.target.value);
                      }}
                    >
                      <option value="">{Localized.LandingSignupTradeSelect}</option>
                      {tradeOptions.map((option) => (
                        <option key={option.key} value={option.key}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className={Styles.formGroup}>
                  <Label text={Localized.LandingSignupPlan} classNameContainer={Styles.formLabel} />
                  <div className={Styles.formSelectWrap}>
                    <select
                      className={Styles.formSelect}
                      value={form.plan || defaultPlanCode}
                      onChange={(event) => {
                        Telemetry.FeatureUsed('landing_signup_plan_changed');
                        update('plan')(event.target.value);
                      }}
                    >
                      {planOptions.map((option) => (
                        <option key={option.key} value={option.key}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              {error ? <Label text={error} classNameContainer={Styles.formError} /> : null}
              <Button text={`${Localized.LandingSignupSubmit} →`} classNameButtonEnabled={`${Styles.formSubmit} ${Styles.ctaScrew}`} onClickHandler={handleSubmit} />
              <Label text={Localized.LandingSignupGuarantee} classNameContainer={Styles.formGuarantee} />
            </div>
          )}
        </div>
      </div>
      <SharedFooter />
    </section>
  );
}
